import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import {
  Code as PythonIcon,
  Storage as SqlIcon,
  TableChart as ExcelIcon,
  TextFields as TextIcon,
  ElectricBolt as DuckDbIcon,
} from '@mui/icons-material';
import CodeRunner from '../shared/CodeRunner';
import StructuredStep from './StructuredStep';

const LANGUAGE_OPTIONS = [
  { value: 'python', label: 'Python', icon: PythonIcon, color: '#4dabf7' },
  { value: 'sql',    label: 'SQLite', icon: SqlIcon,    color: '#69db7c' },
  { value: 'duckdb', label: 'DuckDB', icon: DuckDbIcon, color: '#f0a500' },
  { value: 'excel',  label: 'Excel',  icon: ExcelIcon,  color: '#ffd43b' },
  { value: 'text',   label: 'Describe', icon: TextIcon, color: '#adb5bd' },
];

const EXCEL_DESCRIBE_CONFIG = {
  prompts: [
    'List the exact steps you took in Excel (click by click)',
    'Write the exact formula or feature name you used',
    'What was the result — paste it or describe it precisely',
  ],
};

const CodeRunnerStep = ({ config, itemContext = {}, response = {}, onChange, disabled }) => {
  const defaultLang = config.language || 'python';
  const [language, setLanguage] = useState(defaultLang);
  const prevConfigLang = useRef(defaultLang);
  const allowedLanguages = Array.isArray(config.allowedLanguages) && config.allowedLanguages.length
    ? config.allowedLanguages
    : LANGUAGE_OPTIONS.map((opt) => opt.value);
  const languageOptions = LANGUAGE_OPTIONS.filter((opt) => allowedLanguages.includes(opt.value));

  // useState only initialises once, so if task_type resolves AFTER this mounts
  // (lesson loads async), we need to explicitly sync the language.
  useEffect(() => {
    if (config.language && config.language !== prevConfigLang.current) {
      prevConfigLang.current = config.language;
      setLanguage(config.language);
    }
  }, [config.language]);

  const handleLangChange = (_, next) => {
    if (!next) return;
    setLanguage(next);
    onChange({ ...response, language: next, code: '', output: '', chart: '', error: '' });
  };

  return (
    <Box>
      {/* Language selector */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0 }}>
          Solve with:
        </Typography>
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={handleLangChange}
          size="small"
          disabled={disabled}
          sx={{ flexWrap: 'wrap', gap: 0.5 }}
        >
          {languageOptions.map((opt) => {
            const Icon = opt.icon;
            const isActive = language === opt.value;
            return (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{
                  px: 1.5, py: 0.5,
                  fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700,
                  border: '1px solid',
                  borderColor: isActive ? opt.color : alpha(opt.color, 0.3),
                  color: isActive ? opt.color : 'text.secondary',
                  bgcolor: isActive ? alpha(opt.color, 0.08) : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: alpha(opt.color, 0.1),
                    color: opt.color,
                    borderColor: opt.color,
                    '&:hover': { bgcolor: alpha(opt.color, 0.15) },
                  },
                  '&:hover': { bgcolor: alpha(opt.color, 0.05) },
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Icon sx={{ fontSize: 14 }} />
                  <span>{opt.label}</span>
                </Stack>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        {language !== defaultLang && (
          <Chip
            label={`Default: ${defaultLang}`}
            size="small"
            variant="outlined"
            onDelete={() => handleLangChange(null, defaultLang)}
            sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
          />
        )}
      </Stack>

      {/* Excel = describe mode (can't run in browser) */}
      {language === 'excel' || language === 'text' ? (
        <Box>
          {language === 'excel' && (
            <Box
              sx={{
                px: 1.5, py: 1, mb: 1.5,
                bgcolor: alpha('#ffd43b', 0.07),
                borderLeft: '3px solid #ffd43b',
                borderRadius: '0 4px 4px 0',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Excel runs outside the browser — describe every step precisely enough that someone else could follow it.
              </Typography>
            </Box>
          )}
          <StructuredStep
            config={EXCEL_DESCRIBE_CONFIG}
            response={response}
            onChange={onChange}
            disabled={disabled}
          />
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              px: 1.5, py: 1, mb: 1.5,
              bgcolor: alpha('#4dabf7', 0.07),
              borderLeft: '3px solid #4dabf7',
              borderRadius: '0 4px 4px 0',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              If the full work is already in VS Code/GitHub, reference the file path first, then paste only the smallest runnable proof or key snippet here.
            </Typography>
          </Box>
          <CodeRunner
            value={response.code || ''}
            onChange={(code) => onChange({ ...response, code, language })}
            language={language}
            placeholder={config.placeholder}
            hint={config.hint}
            sampleSql={typeof itemContext.sample_data === 'string' ? itemContext.sample_data : ''}
            csvData={typeof itemContext.csv_data === 'string' ? itemContext.csv_data : ''}
            setupSql={typeof itemContext.setup_sql === 'string' ? itemContext.setup_sql : ''}
            onOutputChange={({ code, output, chart, error }) =>
              onChange({ ...response, code, output, chart, error, language })
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default CodeRunnerStep;
